import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { useBookMyOpAuth } from './BookMyOpAuthContext';
import BookMyOpAPI from '../services/BookMyOpapi';
import config from '../config';

const NotificationContext = createContext();

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  websocket: null,
  isConnected: false
};

const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_NOTIFICATIONS':
      return { ...state, notifications: action.payload };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1
      };
    case 'MARK_READ':
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload ? { ...notif, is_read: true } : notif
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };
    case 'MARK_ALL_READ':
      return {
        ...state,
        notifications: state.notifications.map(notif => ({ ...notif, is_read: true })),
        unreadCount: 0
      };
    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };
    case 'SET_WEBSOCKET':
      return { ...state, websocket: action.payload };
    case 'SET_CONNECTION_STATUS':
      return { ...state, isConnected: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { isAuthenticated } = useBookMyOpAuth();
  const reconnectTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  const lastFetchRef = useRef(0);
  const isInitializedRef = useRef(false);

  // Debounced fetch function to prevent excessive API calls
  const debouncedFetch = useCallback((func, delay = 1000) => {
    const now = Date.now();
    if (now - lastFetchRef.current < delay) {
      return;
    }
    lastFetchRef.current = now;
    func();
  }, []);

  // Combined fetch function to reduce API calls
  const fetchNotificationsAndCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      
      // Fetch both notifications and unread count in parallel
      const [notificationsResponse, unreadCountResponse] = await Promise.all([
        BookMyOpAPI.getNotifications({
          pageSize: 20,
          ordering: '-created_at'
        }),
        BookMyOpAPI.getUnreadNotificationCount()
      ]);
      
      
      dispatch({ type: 'SET_NOTIFICATIONS', payload: notificationsResponse.results || [] });
      // Handle both nested and direct data structures
      const count = unreadCountResponse.unread_count || unreadCountResponse.data?.unread_count || 0;
      dispatch({ type: 'SET_UNREAD_COUNT', payload: count });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to fetch notifications' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [isAuthenticated]);

  // Fetch notifications on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated && !isInitializedRef.current) {
      isInitializedRef.current = true;
      debouncedFetch(fetchNotificationsAndCount, 500);
      setupWebSocket();
    } else if (!isAuthenticated) {
      // Clear notifications when user logs out
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [] });
      dispatch({ type: 'SET_UNREAD_COUNT', payload: 0 });
      closeWebSocket();
      isInitializedRef.current = false;
      // Reset fetch state
      lastFetchRef.current = 0;
    }
  }, [isAuthenticated, fetchNotificationsAndCount, debouncedFetch]);

  // Add cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      closeWebSocket();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      // Reset fetch state on unmount
      lastFetchRef.current = 0;
      isInitializedRef.current = false;
    };
  }, []);

  const fetchNotifications = useCallback(async () => {
    debouncedFetch(fetchNotificationsAndCount, 1000);
  }, [debouncedFetch, fetchNotificationsAndCount]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await BookMyOpAPI.getUnreadNotificationCount();
      // Handle both nested and direct data structures
      const count = response.unread_count || response.data?.unread_count || 0;
      dispatch({ type: 'SET_UNREAD_COUNT', payload: count });
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [isAuthenticated]);

  const setupWebSocket = useCallback(() => {
    if (!isAuthenticated || wsRef.current) return;

    try {
      const accessToken = localStorage.getItem('bookmyop_access_token');
      if (!accessToken) return;

      // Create new WebSocket connection
      const protocol = config.backend.host.startsWith('https') ? 'wss' : 'ws';
      const wsHost = config.backend.ws.host;
      const wsUrl = `${protocol}://${wsHost}/ws/bookmyop/notifications/`;
      
      // Check if WebSocket is supported
      if (!window.WebSocket) {
        console.warn('WebSocket not supported in this browser');
        return;
      }
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected for notifications');
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
        dispatch({ type: 'SET_WEBSOCKET', payload: ws });
        
        // Clear any existing reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification') {
            dispatch({ type: 'ADD_NOTIFICATION', payload: data.notification });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
        wsRef.current = null;
        
        // Attempt to reconnect if not a normal closure and user is still authenticated
        if (event.code !== 1000 && isAuthenticated && !reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isAuthenticated) {
              reconnectTimeoutRef.current = null;
              setupWebSocket();
            }
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.warn('WebSocket connection failed - notifications will work without real-time updates:', error);
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
      };
    } catch (error) {
      console.error('Error setting up WebSocket:', error);
    }
  }, [isAuthenticated]);

  const closeWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
    dispatch({ type: 'SET_WEBSOCKET', payload: null });
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await BookMyOpAPI.markNotificationAsRead(notificationId);
      dispatch({ type: 'MARK_READ', payload: notificationId });
      
      // Update unread count without making a separate API call
      const currentUnreadCount = state.unreadCount;
      dispatch({ type: 'SET_UNREAD_COUNT', payload: Math.max(0, currentUnreadCount - 1) });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to mark notification as read' });
    }
  }, [state.unreadCount]);

  const markAllAsRead = useCallback(async () => {
    try {
      await BookMyOpAPI.markAllNotificationsAsRead();
      dispatch({ type: 'MARK_ALL_READ' });
      dispatch({ type: 'SET_UNREAD_COUNT', payload: 0 });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to mark all notifications as read' });
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value = {
    ...state,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    clearError,
    setupWebSocket,
    closeWebSocket
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 